import classNames from 'classnames';
import type { ChangeEvent } from 'react';

import { AlertWarningIcon, Button, PiggyBankIcon } from '@ctct/payflow-ui';

import './promocodes.scss';
import { Spinner } from '../Spinner';

interface PromoCodeUI {
  name: string;
  displayValue: string;
  isProrated?: boolean;
  isLocked?: boolean;
  isNonDiscount?: boolean;
  onRemove: (name: string) => void;
}

interface Copies {
  singlePromoCodeContainer: {
    heading: string;
    percentageOfYourPlan: string;
    percentageOfYourProratedPlan: string;
    removePromoCodeButtonLabel: string;
  };
  addPromoCodeContainer: {
    heading: string;
    placeholder: string;
    applyButtonLabel: string;
  };
}

interface Props {
  promoCodes: PromoCodeUI[];
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onApply: (name: string) => void;
  disableRemoving?: boolean;
  disableApplying?: boolean;
  isLoading?: boolean;
  errorLabel?: string | null;
  copies: Copies;
  isCompact?: boolean;
  hideButton?: boolean;
}

export const PromoCodes = ({
  promoCodes,
  value,
  onChange,
  onApply,
  disableApplying,
  disableRemoving,
  isLoading,
  errorLabel,
  copies,
  isCompact,
  hideButton,
}: Props) => {
  const { singlePromoCodeContainer, addPromoCodeContainer } = copies;

  return (
    <div
      className={classNames('payflow-presentation promocodes-container', isCompact && 'promocodes-container-compact')}
    >
      {promoCodes.length > 0 ? (
        <div className="promocodes-applied__container">
          {promoCodes.map((promoCode) => (
            <div key={promoCode.name} className="single-promocode-applied__container">
              <div className="single-promocode-applied__icon">
                <PiggyBankIcon />
              </div>
              <div className="single-promocode-applied__content">
                <p className="single-promocode-applied__content_header">{singlePromoCodeContainer.heading}</p>
                <p className="single-promocode-applied__content_info">
                  {promoCode.displayValue}
                  {!promoCode.isNonDiscount &&
                    (promoCode.isProrated
                      ? singlePromoCodeContainer.percentageOfYourProratedPlan
                      : singlePromoCodeContainer.percentageOfYourPlan)}
                </p>
                {!promoCode.isLocked && (
                  <Button
                    variant="text"
                    size="xs"
                    onClick={() => promoCode.onRemove(promoCode.name)}
                    disabled={disableRemoving}
                  >
                    {singlePromoCodeContainer.removePromoCodeButtonLabel}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="promocode-input-container">
        {!isCompact && <p className="promocode-input-container__header">{addPromoCodeContainer.heading}</p>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="promocode-input-container__input-container">
            <div className="promocode-input-container__input-container_input">
              <input
                id="promo-code-input"
                value={value}
                onChange={onChange}
                placeholder={addPromoCodeContainer.placeholder}
              />
              {errorLabel && (
                <span className="promocode-input-container__input-container_error">
                  <AlertWarningIcon /> {errorLabel}
                </span>
              )}
            </div>

            {!hideButton && (
              <Button
                className="promocode-input-container__input-container_apply-btn"
                variant="secondary"
                onClick={() => onApply(value)}
                type="submit"
                disabled={disableApplying || isLoading}
              >
                {isLoading && <Spinner />}
                {addPromoCodeContainer.applyButtonLabel}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
